import { ClientInstallPrompt } from "../../../components/client/ClientInstallPrompt";

type Props = {
  serviceName: string;
  dateTime: string;
  endDateTime?: string;
  staffName: string;
  status?: string;
  branchName?: string;
  onBookAnother: () => void;
};

function toIcsDate(dt: Date): string {
  return dt.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

export function BookingSuccess({ serviceName, dateTime, endDateTime, staffName, status, branchName, onBookAnother }: Props) {
  const formattedDate = dateTime
    ? new Date(dateTime).toLocaleDateString("en", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";
  const formattedTime = dateTime
    ? new Date(dateTime).toLocaleTimeString("en", { hour: "numeric", minute: "2-digit" })
    : "";

  const downloadCalendarInvite = () => {
    if (!dateTime) return;
    const start = new Date(dateTime);
    const end = endDateTime ? new Date(endDateTime) : new Date(start.getTime() + 30 * 60 * 1000);
    const summary = `${serviceName || "Salon Appointment"}${staffName ? ` with ${staffName}` : ""}`;
    const description = `Status: ${status || "pending_confirmation"}`;
    const location = branchName || "Salon";
    const uid = `salonos-${start.getTime()}@local`;
    const now = toIcsDate(new Date());
    const text = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Salon OS//Booking//EN",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      `DTSTART:${toIcsDate(start)}`,
      `DTEND:${toIcsDate(end)}`,
      `SUMMARY:${summary.replace(/\n/g, " ")}`,
      `DESCRIPTION:${description.replace(/\n/g, " ")}`,
      `LOCATION:${location.replace(/\n/g, " ")}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([text], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "salon-appointment.ics";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="step-enter flex flex-col items-center text-center py-6">
      {/* Animated checkmark */}
      <svg className="w-20 h-20 mb-6" viewBox="0 0 52 52">
        <circle
          className="checkmark-circle"
          cx="26"
          cy="26"
          r="25"
          fill="none"
          stroke="var(--client-primary)"
          strokeWidth="2"
        />
        <path
          className="checkmark-check"
          fill="none"
          stroke="var(--client-primary)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M14.1 27.2l7.1 7.2 16.7-16.8"
        />
      </svg>

      <h2 className="text-2xl font-bold text-slate-800 font-heading">Booking Confirmed!</h2>
      <p className="text-slate-500 mt-1">We look forward to seeing you</p>

      {/* Details card */}
      <div className="mt-6 w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 text-left">
        <dl className="grid gap-3 text-sm">
          <div>
            <dt className="text-slate-400 text-xs uppercase tracking-wide">Service</dt>
            <dd className="font-semibold text-slate-800">{serviceName}</dd>
          </div>
          <div>
            <dt className="text-slate-400 text-xs uppercase tracking-wide">Date & Time</dt>
            <dd className="font-semibold text-slate-800">
              {formattedDate} at {formattedTime}
            </dd>
          </div>
          {staffName && (
            <div>
              <dt className="text-slate-400 text-xs uppercase tracking-wide">Specialist</dt>
              <dd className="font-semibold text-slate-800">{staffName}</dd>
            </div>
          )}
          {status && (
            <div>
              <dt className="text-slate-400 text-xs uppercase tracking-wide">Status</dt>
              <dd className="font-semibold text-teal-700 capitalize">{status}</dd>
            </div>
          )}
        </dl>
      </div>

      <button
        onClick={onBookAnother}
        className="mt-6 w-full max-w-sm px-6 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:border-slate-300 hover:bg-slate-50 transition-colors"
      >
        Book Another Appointment
      </button>
      <button
        onClick={downloadCalendarInvite}
        className="mt-3 w-full max-w-sm px-6 py-3 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold hover:border-slate-300 hover:bg-slate-50 transition-colors"
      >
        Add to Calendar (.ics)
      </button>
      <div className="mt-3 w-full max-w-sm">
        <ClientInstallPrompt />
      </div>
    </div>
  );
}
